import { CreditCardIcon, CalendarIcon, LockIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface StripeCardFormProps {
  /**
   * Error message to display
   */
  error?: string

  /**
   * Loading state
   */
  loading?: boolean

  /**
   * Additional CSS classes
   */
  className?: string
}

export function StripeCardForm({
  error,
  loading = false,
  className = "",
}: StripeCardFormProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Card Element Placeholder */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Card Information
        </label>
        <div
          className={cn(
            "relative rounded-lg border bg-background transition-colors",
            error ? "border-destructive" : "border-input",
            loading && "opacity-50 pointer-events-none"
          )}
        >
          {/* This is where Stripe Elements will mount */}
          <div id="stripe-card-element" className="p-3">
            {/* Placeholder UI - Stripe will replace this */}
            <div className="space-y-3">
              {/* Card Number */}
              <div className="flex items-center gap-2 text-muted-foreground">
                <CreditCardIcon className="h-4 w-4" />
                <span className="text-sm">Card number</span>
              </div>

              {/* Expiry and CVC */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CalendarIcon className="h-4 w-4" />
                  <span className="text-sm">MM / YY</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <LockIcon className="h-4 w-4" />
                  <span className="text-sm">CVC</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <span className="inline-block h-1 w-1 rounded-full bg-destructive" />
            {error}
          </p>
        )}

        {/* Helper Text */}
        {!error && (
          <p className="text-xs text-muted-foreground">
            Your card information is encrypted and secure
          </p>
        )}
      </div>

      {/* Security Badge */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-md p-3">
        <LockIcon className="h-3.5 w-3.5" />
        <span>Secure payment powered by Stripe</span>
      </div>
    </div>
  )
}